import React, { useState, useEffect } from 'react';
import { API } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import './BillingInfoSection.css';

function BillingInfoSection({ organizationId }) {
    const { canManage } = usePermissions();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [currentUsage, setCurrentUsage] = useState(null);
    const [organizationInfo, setOrganizationInfo] = useState(null);
    const [useOrgAddress, setUseOrgAddress] = useState(true);
    const [formData, setFormData] = useState({
        billing_company_name: '',
        billing_address: '',
        billing_address_line2: '',
        billing_postal_code: '',
        billing_city: '',
        billing_country: 'France',
        billing_vat_number: '',
        billing_siret: '',
        billing_email: '',
        billing_payment_method: 'bank_transfer'
    });

    useEffect(() => {
        loadData();
        loadCurrentUsage();
    }, [organizationId]);

    const loadData = async () => {
        setLoading(true);
        setError('');
        try {
            // Charger les infos de l'organisation et de facturation en parallele
            const [orgResponse, billingResponse] = await Promise.all([
                API.getOrganizationInfo(organizationId),
                API.getBillingInfo()
            ]);

            // Stocker les infos de l'organisation
            if (orgResponse?.data?.organization) {
                setOrganizationInfo(orgResponse.data.organization);
            }

            // Charger les infos de facturation
            if (billingResponse?.data?.billing_info) {
                const info = billingResponse.data.billing_info;
                const org = orgResponse?.data?.organization;

                // Determiner si l'adresse de facturation est identique a l'organisation
                const isSameAddress = !info.billing_company_name ||
                    (info.billing_company_name === org?.name &&
                     info.billing_address === org?.address &&
                     info.billing_city === org?.city);

                setUseOrgAddress(isSameAddress);

                setFormData({
                    billing_company_name: info.billing_company_name || '',
                    billing_address: info.billing_address || '',
                    billing_address_line2: info.billing_address_line2 || '',
                    billing_postal_code: info.billing_postal_code || '',
                    billing_city: info.billing_city || '',
                    billing_country: info.billing_country || 'France',
                    billing_vat_number: info.billing_vat_number || '',
                    billing_siret: info.billing_siret || '',
                    billing_email: info.billing_email || '',
                    billing_payment_method: info.billing_payment_method || 'bank_transfer'
                });
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Copier les infos de l'organisation vers la facturation
    const copyFromOrganization = () => {
        if (organizationInfo) {
            setFormData(prev => ({
                ...prev,
                billing_company_name: organizationInfo.name || '',
                billing_address: organizationInfo.address || '',
                billing_postal_code: organizationInfo.postal_code || '',
                billing_city: organizationInfo.city || '',
                billing_country: organizationInfo.country || 'France',
                billing_email: organizationInfo.email || ''
            }));
        }
    };

    // Gerer le changement de la checkbox
    const handleUseOrgAddressChange = (e) => {
        const checked = e.target.checked;
        setUseOrgAddress(checked);
        if (checked) {
            copyFromOrganization();
        }
    };

    const loadCurrentUsage = async () => {
        try {
            const response = await API.getCurrentUsage();
            if (response?.data?.usage) {
                setCurrentUsage(response.data.usage);
            }
        } catch (error) {
            console.error('Error loading usage:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            await API.updateBillingInfo(formData);
            setSuccess('Informations de facturation mises a jour');
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            setError(error.message);
        } finally {
            setSaving(false);
        }
    };

    // Formater le SIRET pendant la saisie (XXX XXX XXX XXXXX)
    const formatSiret = (value) => {
        const digits = value.replace(/\D/g, '').slice(0, 14);
        const parts = [];
        if (digits.length > 0) parts.push(digits.slice(0, 3));
        if (digits.length > 3) parts.push(digits.slice(3, 6));
        if (digits.length > 6) parts.push(digits.slice(6, 9));
        if (digits.length > 9) parts.push(digits.slice(9, 14));
        return parts.join(' ');
    };

    const handleSiretChange = (e) => {
        const formatted = formatSiret(e.target.value);
        setFormData(prev => ({ ...prev, billing_siret: formatted }));
    };

    if (loading) {
        return <div className="billing-info-section loading">Chargement...</div>;
    }

    return (
        <div className="billing-info-section">
            {error && <div className="message error">{error}</div>}
            {success && <div className="message success">{success}</div>}

            {/* Usage actuel */}
            {currentUsage && (
                <div className="current-usage-card">
                    <h3>Usage actuel</h3>
                    <div className="usage-stats">
                        <div className="usage-stat">
                            <span className="stat-value">{currentUsage.regular_users_count}</span>
                            <span className="stat-label">Utilisateurs</span>
                        </div>
                        <div className="usage-stat">
                            <span className="stat-value">{currentUsage.shared_mailboxes_count}</span>
                            <span className="stat-label">Boites partagees</span>
                        </div>
                        <div className="usage-stat">
                            <span className="stat-value">{currentUsage.operators_count}</span>
                            <span className="stat-label">Operateurs</span>
                        </div>
                        <div className="usage-stat highlight">
                            <span className="stat-value">{currentUsage.total_billable_units}</span>
                            <span className="stat-label">Total facturable</span>
                        </div>
                    </div>
                    {currentUsage.unit_price > 0 && (
                        <div className="estimated-amount">
                            <span className="estimation-label">Estimation mensuelle</span>
                            <div className="estimation-value">
                                <strong>{currentUsage.estimated_amount_ht.toFixed(2)} EUR HT</strong>
                                <span className="unit-price">{currentUsage.unit_price.toFixed(4)} EUR / unite</span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="form-section">
                    <h3>Informations legales</h3>
                    <p className="section-description">
                        Ces informations apparaitront sur vos factures.
                    </p>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="billing_siret">SIRET</label>
                            <input
                                type="text"
                                id="billing_siret"
                                name="billing_siret"
                                value={formData.billing_siret}
                                onChange={handleSiretChange}
                                placeholder="XXX XXX XXX XXXXX"
                                maxLength={17}
                                disabled={!canManage}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="billing_vat_number">N. TVA intracommunautaire</label>
                            <input
                                type="text"
                                id="billing_vat_number"
                                name="billing_vat_number"
                                value={formData.billing_vat_number}
                                onChange={handleChange}
                                placeholder="FR XX XXXXXXXXX"
                                disabled={!canManage}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Adresse de facturation</h3>

                    <div className="form-group checkbox-inline">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={useOrgAddress}
                                onChange={handleUseOrgAddressChange}
                                disabled={!canManage}
                            />
                            <span>Utiliser les informations de l'organisation</span>
                        </label>
                        {useOrgAddress && organizationInfo && (
                            <div className="org-address-preview">
                                <strong>{organizationInfo.name}</strong>
                                {organizationInfo.address && <><br />{organizationInfo.address}</>}
                                {(organizationInfo.postal_code || organizationInfo.city) && (
                                    <><br />{organizationInfo.postal_code} {organizationInfo.city}</>
                                )}
                            </div>
                        )}
                    </div>

                    {!useOrgAddress && (
                        <>
                            <div className="form-group">
                                <label htmlFor="billing_company_name">Raison sociale</label>
                                <input
                                    type="text"
                                    id="billing_company_name"
                                    name="billing_company_name"
                                    value={formData.billing_company_name}
                                    onChange={handleChange}
                                    placeholder="Nom de l'entreprise"
                                    disabled={!canManage}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="billing_address">Adresse</label>
                                <input
                                    type="text"
                                    id="billing_address"
                                    name="billing_address"
                                    value={formData.billing_address}
                                    onChange={handleChange}
                                    placeholder="Numero et nom de rue"
                                    disabled={!canManage}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="billing_address_line2">Complement d'adresse</label>
                                <input
                                    type="text"
                                    id="billing_address_line2"
                                    name="billing_address_line2"
                                    value={formData.billing_address_line2}
                                    onChange={handleChange}
                                    placeholder="Batiment, etage, etc."
                                    disabled={!canManage}
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="billing_postal_code">Code postal</label>
                                    <input
                                        type="text"
                                        id="billing_postal_code"
                                        name="billing_postal_code"
                                        value={formData.billing_postal_code}
                                        onChange={handleChange}
                                        placeholder="75000"
                                        disabled={!canManage}
                                    />
                                </div>
                                <div className="form-group flex-2">
                                    <label htmlFor="billing_city">Ville</label>
                                    <input
                                        type="text"
                                        id="billing_city"
                                        name="billing_city"
                                        value={formData.billing_city}
                                        onChange={handleChange}
                                        placeholder="Paris"
                                        disabled={!canManage}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="billing_country">Pays</label>
                                <select
                                    id="billing_country"
                                    name="billing_country"
                                    value={formData.billing_country}
                                    onChange={handleChange}
                                    disabled={!canManage}
                                >
                                    <option value="France">France</option>
                                    <option value="Belgique">Belgique</option>
                                    <option value="Suisse">Suisse</option>
                                    <option value="Luxembourg">Luxembourg</option>
                                    <option value="Canada">Canada</option>
                                    <option value="Autre">Autre</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="billing_email">Email de facturation</label>
                                <input
                                    type="email"
                                    id="billing_email"
                                    name="billing_email"
                                    value={formData.billing_email}
                                    onChange={handleChange}
                                    placeholder="comptabilite@entreprise.com"
                                    disabled={!canManage}
                                />
                            </div>
                        </>
                    )}
                </div>

                <div className="form-section">
                    <h3>Mode de paiement</h3>

                    <div className="form-group">
                        <label htmlFor="billing_payment_method">Methode preferee</label>
                        <select
                            id="billing_payment_method"
                            name="billing_payment_method"
                            value={formData.billing_payment_method}
                            onChange={handleChange}
                            disabled={!canManage}
                        >
                            <option value="bank_transfer">Virement bancaire</option>
                            <option value="direct_debit">Prelevement automatique</option>
                            <option value="card">Carte bancaire</option>
                        </select>
                    </div>
                </div>

                {canManage && (
                    <div className="form-actions">
                        <button type="submit" className="btn-primary" disabled={saving}>
                            {saving ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
}

export default BillingInfoSection;
